import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import { Skeleton } from "@app/components/shared/Skeleton";
import type {
  AiPerformanceReport,
  AutomationGuardResponse,
  AutomationRiskLevel,
  PerformanceMetric,
  SupervisorRecommendation,
} from "@app/services/aiSupervisorApi";
import {
  useAutomationGuardAssessment,
  usePerformanceSnapshot,
  useSupervisorDashboard,
} from "@app/features/ai-supervisor/hooks/useAiSupervisorData";
import "./aiSupervisor.css";

type GuardFormState = {
  action: string;
  description: string;
  roleId: string;
  planId: string;
  focusSymbol: string;
  requestedBy: string;
  riskLevel: AutomationRiskLevel;
  dryRun: boolean;
  performanceWindowHours: number;
};

const DEFAULT_GUARD_STATE: GuardFormState = {
  action: "auto_execute_strategy",
  description: "",
  roleId: "admin",
  planId: "enterprise",
  focusSymbol: "",
  requestedBy: "supervisor",
  riskLevel: "medium",
  dryRun: false,
  performanceWindowHours: 24,
};

const RISK_LEVEL_OPTIONS: Array<{ value: AutomationRiskLevel; label: string }> = [
  { value: "low", label: "Low impact" },
  { value: "medium", label: "Medium impact" },
  { value: "high", label: "High impact" },
  { value: "critical", label: "Critical / irreversible" },
];

const PERFORMANCE_WINDOWS = [6, 12, 24, 48, 72];

function formatDate(value?: string | null) {
  if (!value) return "—";
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) {
    return value;
  }
  // Use fixed format independent of browser/OS locale: YYYY-MM-DD HH:mm:ss
  const year = timestamp.getFullYear();
  const month = String(timestamp.getMonth() + 1).padStart(2, "0");
  const day = String(timestamp.getDate()).padStart(2, "0");
  const hours = String(timestamp.getHours()).padStart(2, "0");
  const minutes = String(timestamp.getMinutes()).padStart(2, "0");
  const seconds = String(timestamp.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function formatPercent(value?: number | null) {
  if (value === undefined || value === null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function metricTone(metricId: string, value: number): "accent" | "success" | "warning" | "danger" | "neutral" {
  if (metricId.includes("success") || metricId.includes("win")) {
    if (value >= 70) return "success";
    if (value >= 40) return "warning";
    return "danger";
  }
  if (metricId.includes("pnl")) {
    if (value >= 0) return "success";
    if (value >= -5) return "warning";
    return "danger";
  }
  return "accent";
}

function overallTone(status?: "info" | "warning" | "critical"): "accent" | "warning" | "danger" {
  switch (status) {
    case "critical":
      return "danger";
    case "warning":
      return "warning";
    default:
      return "accent";
  }
}

function GuardOutcome({ result }: { result: AutomationGuardResponse }) {
  return (
    <div className="ai-supervisor-card-list">
      <div className="ai-supervisor-status">
        <Pill tone={result.allowed ? "success" : result.requiresManualReview ? "warning" : "danger"}>
          {result.allowed ? "Automation approved" : result.requiresManualReview ? "Manual review required" : "Automation blocked"}
        </Pill>
        <span>Risk level: {result.riskLevel.toUpperCase()}</span>
      </div>
      <div className="ai-supervisor-inline-list">
        <span>Authority: {result.permissionProfile.authorityLevel}</span>
        <span>Automation mode: {result.permissionProfile.automationMode}</span>
        {result.triggeredPolicies.map((policy) => (
          <span key={policy}>Policy: {policy}</span>
        ))}
      </div>
      {result.guardrails.length > 0 ? (
        <div className="ai-supervisor-notes">
          <strong>Guardrails applied:</strong>
          <ul>
            {result.guardrails.map((guardrail) => (
              <li key={guardrail}>{guardrail}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="ai-supervisor-notes">
        <strong>Rollback plan ({result.rollbackPlan.planId}):</strong>
        <ol>
          {result.rollbackPlan.steps.map((step) => (
            <li key={step.id}>
              <strong>{step.label}</strong> — {step.description ?? "No description"} ({step.priority} priority)
            </li>
          ))}
        </ol>
      </div>
      {result.notes.length > 0 ? (
        <div className="ai-supervisor-notes">
          <strong>Supervisor notes:</strong>
          <ul>
            {result.notes.map((note, index) => (
              <li key={`${note}-${index}`}>{note}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="ai-supervisor-notes">
        <strong>Evaluated:</strong> {formatDate(result.generatedAt)}
      </div>
    </div>
  );
}

export function AiSupervisorTab(): JSX.Element {
  const [guardState, setGuardState] = useState<GuardFormState>(DEFAULT_GUARD_STATE);
  const [performancePeriod, setPerformancePeriod] = useState(24);

  const {
    data: dashboard,
    isLoading: isDashboardLoading,
    isError: dashboardError,
    error: dashboardErrorDetails,
    refetch: refetchDashboard,
  } = useSupervisorDashboard();
  const {
    data: performance,
    isLoading: isPerformanceLoading,
    refetch: refetchPerformance,
  } = usePerformanceSnapshot(performancePeriod);
  const guardAssessment = useAutomationGuardAssessment();

  const overallStatusTone = useMemo(() => overallTone(dashboard?.overallStatus), [dashboard?.overallStatus]);
  const recommendations: SupervisorRecommendation[] = dashboard?.recommendations ?? [];
  const triggeredSignals = dashboard?.triggeredSignals ?? [];
  const performanceReport: AiPerformanceReport | undefined = performance;
  const performanceMetrics: PerformanceMetric[] = performanceReport?.metrics ?? [];

  const handleGuardInputChange = <Key extends keyof GuardFormState>(field: Key, value: GuardFormState[Key]) => {
    setGuardState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGuardSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    guardAssessment.mutate({
      action: guardState.action.trim(),
      description: guardState.description.trim() || undefined,
      roleId: guardState.roleId.trim() || undefined,
      planId: guardState.planId.trim() || undefined,
      requestedBy: guardState.requestedBy.trim() || undefined,
      riskLevel: guardState.riskLevel,
      dryRun: guardState.dryRun,
      performanceWindowHours: guardState.performanceWindowHours,
      context: guardState.focusSymbol.trim() ? { symbol: guardState.focusSymbol.trim() } : undefined,
    });
  };

  return (
    <div className="ai-supervisor-root">
      <header className="ai-supervisor-header">
        <div className="ai-supervisor-header__meta">
          <h2>AI Supervisor Console</h2>
          <p>Monitor the trading brain, enforce automation guardrails, and review the latest intelligence.</p>
          {isDashboardLoading ? (
            <Skeleton style={{ height: "1.25rem", width: "220px" }} />
          ) : dashboard ? (
            <div className={`ai-supervisor-status ai-supervisor-status--${dashboard.overallStatus}`}>
              <Pill tone={overallStatusTone}>Overall status: {dashboard.overallStatus}</Pill>
              <span>Generated {formatDate(dashboard.generatedAt)}</span>
            </div>
          ) : (
            <div className="ai-supervisor-empty">Unable to load supervisor data.</div>
          )}
        </div>
        <div className="ai-supervisor-inline-list">
          <Button type="button" variant="ghost" onClick={() => refetchDashboard()}>
            Refresh dashboard
          </Button>
          <Button type="button" variant="ghost" onClick={() => refetchPerformance()}>
            Refresh performance
          </Button>
        </div>
      </header>

      {dashboardError ? (
        <div className="ai-supervisor-empty">
          Failed to load dashboard: {(dashboardErrorDetails as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      <section className="ai-supervisor-grid">
        <Card
          className="overview-card"
          title="Automation guard evaluation"
          subtitle="Assess risk before enabling automation or emergency responses."
        >
          <form className="ai-supervisor-automation-form" onSubmit={handleGuardSubmit}>
            <div className="ai-supervisor-automation-form__row">
              <label>
                Automation action
                <input
                  required
                  value={guardState.action}
                  onChange={(event) => handleGuardInputChange("action", event.target.value)}
                  placeholder="e.g. auto_execute_strategy"
                />
              </label>
              <label>
                Target role
                <input
                  value={guardState.roleId}
                  onChange={(event) => handleGuardInputChange("roleId", event.target.value)}
                  placeholder="admin / analyst / trader"
                />
              </label>
              <label>
                Subscription plan
                <input
                  value={guardState.planId}
                  onChange={(event) => handleGuardInputChange("planId", event.target.value)}
                  placeholder="enterprise / pro / starter"
                />
              </label>
            </div>
            <div className="ai-supervisor-automation-form__row">
              <label>
                Risk profile
                <select
                  value={guardState.riskLevel}
                  onChange={(event) => handleGuardInputChange("riskLevel", event.target.value as AutomationRiskLevel)}
                >
                  {RISK_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Performance window
                <select
                  value={guardState.performanceWindowHours}
                  onChange={(event) =>
                    handleGuardInputChange("performanceWindowHours", Number(event.target.value))
                  }
                >
                  {PERFORMANCE_WINDOWS.map((hours) => (
                    <option key={hours} value={hours}>
                      Last {hours} hours
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Focus symbol (optional)
                <input
                  value={guardState.focusSymbol}
                  onChange={(event) => handleGuardInputChange("focusSymbol", event.target.value)}
                  placeholder="e.g. BTCUSDT"
                />
              </label>
            </div>
            <label>
              Short description
              <textarea
                value={guardState.description}
                onChange={(event) => handleGuardInputChange("description", event.target.value)}
                placeholder="Why are we triggering this automation?"
              />
            </label>
            <div className="ai-supervisor-automation-form__row">
              <label>
                Requested by
                <input
                  value={guardState.requestedBy}
                  onChange={(event) => handleGuardInputChange("requestedBy", event.target.value)}
                  placeholder="Operator name"
                />
              </label>
              <label>
                <span>
                  Dry run
                  <small>(Evaluate guardrails without enabling automation)</small>
                </span>
                <select
                  value={guardState.dryRun ? "true" : "false"}
                  onChange={(event) => handleGuardInputChange("dryRun", event.target.value === "true")}
                >
                  <option value="false">Execute normally</option>
                  <option value="true">Dry run only</option>
                </select>
              </label>
            </div>
            <div className="ai-supervisor-automation-form__footer">
              <Button type="submit" variant="primary" disabled={guardAssessment.isPending}>
                {guardAssessment.isPending ? "Evaluating…" : "Evaluate guards"}
              </Button>
              {guardAssessment.isError ? (
                <span className="ai-supervisor-empty">
                  {(guardAssessment.error as Error)?.message ?? "Automation guard evaluation failed."}
                </span>
              ) : null}
            </div>
          </form>
          {guardAssessment.data ? (
            <GuardOutcome result={guardAssessment.data} />
          ) : (
            <div className="ai-supervisor-empty">
              Submit an automation request to preview guardrails, risk level, and rollback plan.
            </div>
          )}
        </Card>

        <Card
          className="overview-card"
          title="AI performance snapshot"
          subtitle="Latest outcomes from automated trading decisions and risk actions."
        >
          {isPerformanceLoading ? (
            <Skeleton style={{ height: "140px" }} />
          ) : performanceReport ? (
            <div className="ai-supervisor-card-list">
              <div className="ai-supervisor-inline-list">
                <span>Window: last {performanceReport.periodHours} hours</span>
                <span>Knowledge version: {performanceReport.knowledgeVersion.versionId.slice(0, 12)}…</span>
                <span>Knowledge files: {performanceReport.knowledgeVersion.fileCount}</span>
              </div>
              <div className="ai-supervisor-metrics">
                <div className="ai-supervisor-metric">
                  <span className="ai-supervisor-metric__label">Decision success rate</span>
                  <span className="ai-supervisor-metric__value">
                    {formatPercent(performanceReport.decisionSummary.successRate)}
                  </span>
                </div>
                <div className="ai-supervisor-metric">
                  <span className="ai-supervisor-metric__label">Decisions pending review</span>
                  <span className="ai-supervisor-metric__value">{performanceReport.decisionSummary.pending}</span>
                </div>
                <div className="ai-supervisor-metric">
                  <span className="ai-supervisor-metric__label">Trades executed</span>
                  <span className="ai-supervisor-metric__value">{performanceReport.tradeSummary.executed}</span>
                </div>
                <div className="ai-supervisor-metric">
                  <span className="ai-supervisor-metric__label">Trade win rate</span>
                  <span className="ai-supervisor-metric__value">
                    {formatPercent(performanceReport.tradeSummary.winRate)}
                  </span>
                </div>
                <div className="ai-supervisor-metric">
                  <span className="ai-supervisor-metric__label">Net PnL</span>
                  <span className="ai-supervisor-metric__value">
                    {performanceReport.tradeSummary.netPnl !== undefined && performanceReport.tradeSummary.netPnl !== null
                      ? `${performanceReport.tradeSummary.netPnl.toFixed(2)}`
                      : "—"}
                  </span>
                </div>
              </div>
              {performanceMetrics.length > 0 ? (
                <div className="ai-supervisor-inline-list">
                  {performanceMetrics.map((metric) => (
                    <Pill key={metric.id} tone={metricTone(metric.id, metric.value)}>
                      {metric.label}: {metric.unit === "%" ? metric.value.toFixed(1) + metric.unit : metric.value}
                    </Pill>
                  ))}
                </div>
              ) : null}
              {performanceReport.notes.length > 0 ? (
                <div className="ai-supervisor-notes">
                  <strong>Observations:</strong>
                  <ul>
                    {performanceReport.notes.map((note, index) => (
                      <li key={`${note}-${index}`}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="ai-supervisor-empty">Performance telemetry will appear once executions are recorded.</div>
          )}
          <div className="ai-supervisor-inline-list" style={{ marginTop: "1rem" }}>
            <span>Select window:</span>
            {PERFORMANCE_WINDOWS.map((hours) => (
              <Button
                key={`window-${hours}`}
                type="button"
                variant={performancePeriod === hours ? "primary" : "ghost"}
                onClick={() => setPerformancePeriod(hours)}
              >
                {hours}h
              </Button>
            ))}
          </div>
        </Card>

        <Card
          className="overview-card"
          title="Active AI recommendations"
          subtitle="Opportunities surfaced by the trading brain ranked by confidence."
        >
          {isDashboardLoading ? (
            <Skeleton style={{ height: "160px" }} />
          ) : recommendations.length > 0 ? (
            <ul className="ai-supervisor-list">
              {recommendations.map((rec) => (
                <li key={`${rec.symbol}-${rec.action}`} className="ai-supervisor-list__item">
                  <strong>
                    {rec.symbol} · {rec.action.toUpperCase()}
                  </strong>
                  <span>
                    Score {rec.score.toFixed(2)} · Confidence {rec.confidence.toFixed(2)}
                  </span>
                  {rec.reasons.length > 0 ? (
                    <div className="ai-supervisor-inline-list">
                      {rec.reasons.map((reason, index) => (
                        <span key={`${rec.symbol}-reason-${index}`}>{reason}</span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className="ai-supervisor-empty">No active recommendations; the AI is scanning the market for new opportunities.</div>
          )}
        </Card>

        <Card
          className="overview-card"
          title="Triggered signals"
          subtitle="Unified market and risk signals informing the current status."
        >
          {isDashboardLoading ? (
            <Skeleton style={{ height: "160px" }} />
          ) : triggeredSignals.length > 0 ? (
            <ul className="ai-supervisor-list">
              {triggeredSignals.map((signal) => (
                <li key={signal.id} className="ai-supervisor-list__item">
                  <div className="ai-supervisor-inline-list">
                    <Pill tone={overallTone(signal.severity)}>
                      {signal.severity.toUpperCase()}
                    </Pill>
                    <span>{signal.category}</span>
                  </div>
                  <strong>{signal.label}</strong>
                  <span>{signal.summary ?? signal.value ?? signal.threshold ?? "No summary available."}</span>
                  {signal.sources && signal.sources.length > 0 ? (
                    <div className="ai-supervisor-inline-list">
                      {signal.sources.map((source) => (
                        <span key={`${signal.id}-${source}`}>{source}</span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className="ai-supervisor-empty">No critical signals detected.</div>
          )}
        </Card>

        <Card
          className="overview-card"
          title="Role & plan alignment"
          subtitle="Capabilities available to this workspace based on the active plan."
        >
          {isDashboardLoading ? (
            <Skeleton style={{ height: "140px" }} />
          ) : dashboard?.rolePlan ? (
            <div className="ai-supervisor-card-list">
              <div className="ai-supervisor-inline-list">
                <span>Role catalog: {dashboard.rolePlan.roles.length}</span>
                <span>Plans: {dashboard.rolePlan.plans.length}</span>
                <span>Last sync: {formatDate(dashboard.rolePlan.generatedAt)}</span>
              </div>
              <div className="ai-supervisor-list">
                {dashboard.rolePlan.roles.map((role) => (
                  <div key={role.id} className="ai-supervisor-list__item">
                    <strong>{role.label}</strong>
                    <span>{role.description ?? "No description provided."}</span>
                    <div className="ai-supervisor-inline-list">
                      {role.permissions.slice(0, 6).map((permission) => (
                        <span key={`${role.id}-${permission}`}>{permission}</span>
                      ))}
                      {role.permissions.length === 0 ? <span>No explicit permissions.</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="ai-supervisor-empty">Role information has not loaded yet.</div>
          )}
        </Card>
      </section>
    </div>
  );
}

